import React from 'react';
import Skeleton, { SkeletonTable, SkeletonChart } from './Skeleton';
import {
	InstructionManualPageSkeleton,
	NewsFeedsPageSkeleton,
	PhishingMaterialPageSkeleton,
	PosterPageSkeleton,
	ProfilePageSkeleton,
	SimpleTemplatesPageSkeleton,
	SystemNotifiersPageSkeleton,
	TrainingModulesPageSkeleton,
} from './PageSkeletons';
import LoginLoader from './LoginLoader';
import './ShellSkeleton.scss';

/**
 * Route-shaped loading states (guide §15). These mirror the REAL page markup —
 * reusing the app's own layout classes (.sidebar-container, .wrapper,
 * .header-container, .body, .card-container, .client-dashboard-card, .card…) —
 * so every skeleton block sits exactly where its real content will, then fills
 * with shimmer bars.
 */

const rnd = (i, lo, hi) => lo + ((i * 37) % (hi - lo));

/* ---------------- content-only page skeletons ---------------- */

const Card = ({ header, children, className = '' }) => (
	<div className={`card ${className}`.trim()}>
		{header ? <div className="card-header">{header}</div> : null}
		<div className="card-body">{children}</div>
	</div>
);

export const DashboardSkeleton = () => (
	<div className="card-container" role="status" aria-busy="true" aria-label="Loading">
		{/* greeting */}
		<Skeleton width={240} height={16} className="mb-3" />
		{/* stats band (PhishingStats / risk gauge) */}
		<div className="row g-3 mb-1">
			<div className="col-12 col-lg-8 mb-3">
				<Card>
					<div className="ivs-sk-statband">
						{[0, 1, 2].map((i) => (
							<div className="ivs-sk-stat" key={i}>
								<Skeleton width="60%" height={11} />
								<Skeleton width="45%" height={26} />
								<Skeleton width="80%" height={10} />
							</div>
						))}
					</div>
				</Card>
			</div>
			<div className="col-12 col-lg-4 mb-3">
				<Card>
					<div className="ivs-sk-gauge">
						<Skeleton width={120} height={120} radius={999} />
					</div>
				</Card>
			</div>
		</div>
		{/* two trend charts */}
		<div className="row g-3 mb-1">
			{[0, 1].map((i) => (
				<div className="col-12 col-lg-6 mb-3" key={i}>
					<Card header={<Skeleton width={190} height={14} />}>
						<SkeletonChart height={210} />
					</Card>
				</div>
			))}
		</div>
		{/* KPI row — 4 cards, col-sm-3, matching .client-dashboard-card */}
		<div className="row g-3">
			{[0, 1, 2, 3].map((i) => (
				<div className="col-sm-3 mb-3" key={i}>
					<div className="card client-dashboard-card">
						<div className="card-body">
							<div className="ivs-sk-kpi">
								<Skeleton width={46} height={46} radius={10} />
								<div className="ivs-sk-kpi__txt">
									<Skeleton width="55%" height={26} />
									<Skeleton width="80%" height={12} />
								</div>
							</div>
							<hr className="ivs-sk-div" />
							<Skeleton width={110} height={12} />
						</div>
					</div>
				</div>
			))}
		</div>
		{/* chart + campaign table */}
		<div className="row g-3">
			<div className="col-12 col-lg-7 mb-3">
				<Card header={<Skeleton width={200} height={14} />}>
					<SkeletonChart height={230} />
				</Card>
			</div>
			<div className="col-12 col-lg-5 mb-3">
				<Card header={<Skeleton width={160} height={14} />}>
					<SkeletonChart height={230} />
				</Card>
			</div>
		</div>
		<div className="row g-3">
			<div className="col-12">
				<Card header={<Skeleton width={160} height={14} />}>
					<SkeletonTable cols={6} rows={6} />
				</Card>
			</div>
		</div>
	</div>
);

export const FormSkeleton = ({ rows = 6 }) => (
	<div className="card-container" role="status" aria-busy="true" aria-label="Loading">
		<div className="ivs-sk-pagehead">
			<Skeleton width={220} height={22} />
		</div>
		<Card>
			<div className="ivs-sk-form">
				{Array.from({ length: rows }).map((_, i) => (
					<div className="ivs-sk-field" key={i}>
						<Skeleton width={130} height={12} />
						<Skeleton width="100%" height={40} radius={8} />
					</div>
				))}
			</div>
			<div className="ivs-sk-formactions">
				<Skeleton width={110} height={40} radius={8} />
				<Skeleton width={90} height={40} radius={8} />
			</div>
		</Card>
	</div>
);

export const ListPageSkeleton = () => (
	<div className="card-container" role="status" aria-busy="true" aria-label="Loading">
		<div className="ivs-sk-pagehead">
			<Skeleton width={200} height={22} />
			<Skeleton width={150} height={36} radius={8} />
		</div>
		<Card>
			<div className="ivs-sk-toolbar">
				<Skeleton width={260} height={38} radius={8} />
				<span className="ivs-sk-spacer" />
				<Skeleton width={120} height={38} radius={8} />
			</div>
			<SkeletonTable cols={6} rows={10} />
		</Card>
	</div>
);

export const CardPageSkeleton = () => (
	<div className="card-container" role="status" aria-busy="true" aria-label="Loading">
		<div className="ivs-sk-pagehead">
			<Skeleton width={220} height={38} radius={8} />
			<Skeleton width={170} height={38} radius={8} />
		</div>
		<div className="row g-3">
			{Array.from({ length: 9 }).map((_, i) => (
				<div className="col-12 col-sm-6 col-lg-4 mb-3" key={i}>
					<div className="card">
						<div className="card-header">
							<Skeleton width={`${50 + rnd(i, 10, 35)}%`} height={14} />
						</div>
						<div className="card-body p-0">
							<Skeleton width="100%" height={190} radius={0} />
						</div>
					</div>
				</div>
			))}
		</div>
	</div>
);

export const ContentSkeleton = () => (
	<div className="card-container" role="status" aria-busy="true" aria-label="Loading">
		<Skeleton width={210} height={22} className="mb-3" />
		<Card>
			<div className="ivs-sk-lines" style={{ gap: 12 }}>
				{Array.from({ length: 6 }).map((_, i) => (
					<Skeleton key={i} width={`${90 - rnd(i, 0, 45)}%`} height={14} />
				))}
			</div>
		</Card>
	</div>
);

// Pages with their own page-shaped skeleton (same markup as the page's loading state).
// Matched on the end of the path so a deployment prefix (PREFIX_PATH) doesn't matter.
const PAGE_SKELETONS = [
	['/instructionmanual', () => <InstructionManualPageSkeleton />],
	['/newsfeeds', () => <NewsFeedsPageSkeleton />],
	['/profile', () => <ProfilePageSkeleton />],
	['/training-modules', () => <TrainingModulesPageSkeleton />],
	['/system/templates', () => <SystemNotifiersPageSkeleton />],
	['/email/templates', () => <SimpleTemplatesPageSkeleton headerClassName="header-title" />],
	['/sms/templates', () => <SimpleTemplatesPageSkeleton />],
	['/voice/templates', () => <ListPageSkeleton />],
	['/manage/posters', () => <PosterPageSkeleton title="Posters" />],
	['/manage/landing-pages', () => <PosterPageSkeleton title="Landing Page" />],
	['/landing-pages', () => <SimpleTemplatesPageSkeleton />],
	['/templates', () => <PhishingMaterialPageSkeleton />],
];

/* route → matching content skeleton */
export const routeSkeletonFor = (path = '') => {
	const p = (path || '').toLowerCase().replace(/\/+$/, '');
	const page = PAGE_SKELETONS.find(([suffix]) => p.endsWith(suffix));
	if (page) return page[1]();
	if (p.includes('dashboard')) return <DashboardSkeleton />;
	// forms: create / edit / new / configure / profile / settings
	if (/(create|\/edit|\/new|duplicate|configure|customize|profile|settings|\/reset)/.test(p)) return <FormSkeleton />;
	if (/(template|landing|poster)/.test(p)) return <CardPageSkeleton />;
	if (/(customer|user|employee|address|categor|api-key|whatsapp|report|reminder|offender|campaign|feed)/.test(p)) return <ListPageSkeleton />;
	return <ContentSkeleton />;
};

const currentPath = () => {
	try {
		return window.location.pathname || '';
	} catch (e) {
		return '';
	}
};

/* ---------------- full-window shell (mirrors DefaultLayout) ---------------- */

const navModel = [
	't', 1, 1, 1, 't', 1, 1, 1, 1, 't', 1, 1, 1, 't', 1, 1,
];

export const AppShellSkeleton = ({ path }) => (
	<div className="ivs-shellsk" role="status" aria-busy="true" aria-label="Loading">
		<div className="sidebar-container">
			<div className="sidebar-brand">
				<Skeleton width={150} height={26} radius={6} />
			</div>
			<div className="sidebar-nav ivs-sk-nav">
				{navModel.map((row, i) =>
					row === 't' ? (
						<div className="ivs-sk-navtitle" key={i}>
							<Skeleton width={`${30 + rnd(i, 10, 25)}%`} height={9} />
						</div>
					) : (
						<div className="ivs-sk-navlink" key={i}>
							<Skeleton width={18} height={18} radius={5} />
							<Skeleton width={`${45 + rnd(i, 5, 35)}%`} height={12} />
						</div>
					),
				)}
			</div>
		</div>
		<div className="wrapper d-flex flex-column min-vh-100">
			<div className="header-container ivs-sk-header">
				<span className="ivs-sk-spacer" />
				<Skeleton width={34} height={34} radius={8} />
				<Skeleton width={34} height={34} radius={8} />
				<Skeleton width={120} height={34} radius={999} />
			</div>
			<div className="body flex-grow-1">
				{/* Same structure as AppContent: breadcrumb bar, then the page in .content-area */}
				<div className="content-wrapper">
					<div className="ivs-sk-crumb">
						<Skeleton width={150} height={20} />
					</div>
					<div className="content-area">{routeSkeletonFor(path || currentPath())}</div>
				</div>
			</div>
		</div>
	</div>
);

const PUBLIC_HINTS = ['/login', '/forgot', '/reset', '/first-login'];

export const RouteFallback = () => {
	const p = currentPath().toLowerCase();
	const isPublic = PUBLIC_HINTS.some((h) => p.includes(h)) || p === '/' || p.endsWith('/');
	return isPublic ? <LoginLoader /> : <AppShellSkeleton path={p} />;
};

export default AppShellSkeleton;
